import { pageInfoFragment } from "@saleor/fragments/pageInfo";
import makeBossQuery from "@saleor/hooks/makeBossQuery";
import makeQuery from "@saleor/hooks/makeQuery";
import gql from "graphql-tag";

import { TypedQuery } from "../queries";
import { BrandList, BrandListVariables } from "./types/BrandList";
import {
  TenantInStoreCancelConfig,
  TenantInStoreCancelConfigVariables
} from "./types/InStoreCancelConfig";
import { MallList, MallListVariables } from "./types/MallList";
import { PaymentFeeRate } from "./types/PaymentFeeRate";
import { TenantDetails, TenantDetailsVariables } from "./types/TenantDetails";
import { TenantList, TenantListVariables } from "./types/TenantList";

const getMallList = gql`
  ${pageInfoFragment}
  query MallList($first: Int, $last: Int, $before: String, $after: String) {
    malls(first: $first, last: $last, before: $before, after: $after) {
      edges {
        node {
          id
          address {
            id
          }
          name
          code
          contactPerson
          mobileNo
          telephoneNo
          email
          totalLandArea
          status
        }
      }
      pageInfo {
        ...PageInfoFragment
      }
    }
  }
`;

const getBrandList = gql`
  ${pageInfoFragment}
  query BrandList(
    $first: Int
    $last: Int
    $before: String
    $after: String
    $status: SmopStatusEnum
  ) {
    brandsPrivate(
      first: $first
      last: $last
      before: $before
      after: $after
      status: $status
    ) {
      edges {
        node {
          id
          name
          code
          legalName
          storeType
        }
      }
      pageInfo {
        ...PageInfoFragment
      }
    }
  }
`;

const getTenantList = gql`
  ${pageInfoFragment}
  query TenantList(
    $first: Int
    $last: Int
    $before: String
    $after: String
    $filter: TenantInput
    $status: SmopStatusEnum
  ) {
    tenantsPrivate(
      before: $before
      after: $after
      first: $first
      last: $last
      filter: $filter
      status: $status
    ) {
      edges {
        node {
          id
          storeId
          brand {
            id
            name
            legalName
            bankName
            bankAccountNo
            bankAccountName
            financeName
            financeEmail
            financeMobileNo
          }
          mall {
            id
            name
          }
          legalName
          unitNo
          code
          status
          contractId
          tenantType
          description
          status
          operationStatus
          operationStartTime
          operationEndTime
          operationScheduleMeta
          dateCreated
          isExpressLongDistanceDeliveryAllowed
          isOtherBusiness
          ipay88SubMid
          isSameDayDeliveryAllowed
          tinNo
          completeAddress
          signatoryName
          signatoryEmail
          signatoryDesignation
          signatoryMobileNo
          bankName
          bankAccountName
          bankAccountNo
          financeName
          financeEmail
          financeMobileNo
          storeName
          paymentFeeRate
          promoRibbonDescription
          promoRibbonStart
          promoRibbonEnd
          isFeatured
          paymentFeeRate
        }
      }
      pageInfo {
        ...PageInfoFragment
      }
    }
  }
`;

const getTenant = gql`
  query TenantDetails($id: ID!) {
    tenantPrivate(id: $id) {
      id
      storeId
      brand {
        id
        name
        bankName
        bankAccountNo
        bankAccountName
        financeName
        financeEmail
        financeMobileNo
      }
      mall {
        id
        name
        isInMallPickup
        nextDayAvailable
      }
      cluster {
        id
        name
      }
      bannerImage {
        url
        alt
      }
      isUseTenantCluster
      location
      legalName
      unitNo
      code
      leaseNo
      contractId
      tenantType
      description
      status
      operationStatus
      operationStartTime
      operationEndTime
      operationScheduleMeta
      dateCreated
      isExpressLongDistanceDeliveryAllowed
      isFeatured
      isSameDayDeliveryAllowed
      isStandardDeliveryEnabled
      isInStorePickupEnabled
      isOtherBusiness
      ipay88SubMid
      tinNo
      completeAddress
      signatoryName
      signatoryEmail
      signatoryDesignation
      signatoryMobileNo
      bankName
      bankAccountName
      bankAccountNo
      financeName
      financeEmail
      financeMobileNo
      rentalType
      paymentFeeRate
      promoRibbonDescription
      promoRibbonStart
      promoRibbonEnd
      storeName
      tinNo
      subTenantType
    }
  }
`;

export const useMallListQuery = makeQuery<MallList, MallListVariables>(
  getMallList
);

export const useBrandQuery = makeQuery<BrandList, BrandListVariables>(
  getBrandList
);

export const useTenantListQuery = makeQuery<TenantList, TenantListVariables>(
  getTenantList
);

export const UseTenantDetailsQuery = TypedQuery<
  TenantDetails,
  TenantDetailsVariables
>(getTenant);

const paymentFeeRate = gql`
  query PaymentFeeRate {
    paymentFee {
      rate
    }
  }
`;

export const usePaymentFeeRate = makeBossQuery<PaymentFeeRate, "">(
  paymentFeeRate
);

const getTenantInStoreCancelConfig = gql`
  query TenantInStoreCancelConfig($tenantId: ID!) {
    tenantInStoreCancelConfig(tenantId: $tenantId) {
      id
      isEnabled
      cancellationDays
      createdAt
      updatedAt
      allowNationwideDiscovery
    }
  }
`;

export const useTenantInStoreCancelConfigQuery = makeQuery<
  TenantInStoreCancelConfig,
  TenantInStoreCancelConfigVariables
>(getTenantInStoreCancelConfig);
